import { Fingerprint } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import { InteractionEvent } from '@logto/schemas';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import classNames from 'classnames';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedLoader } from 'use-debounced-loader';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import WebAuthnContext from '@/Providers/WebAuthnContextProvider/WebAuthnContext';
import { LastUsedFrame } from '@/components/LastUsedChip';
import useLastUsedSignInMethod from '@/hooks/use-last-used-sign-in-method';
import usePasskeySignIn from '@/hooks/use-passkey-sign-in';
import useSubmitInteractionErrorHandler from '@/hooks/use-submit-interaction-error-handler';
import MatrixLoader from '@/shared/components/MatrixLoader';

import { optionButtonClass, optionButtonProps, optionLabelClass } from '../option-button-class';

const PasskeySignInButton = () => {
  const { t } = useTranslation();
  const { isPreview } = useContext(PageContext);
  const {
    authenticationOptions,
    isLoading: isPreparing,
    isPasskeyFlowProcessing,
    setIsPasskeyFlowProcessing,
    markAuthenticationOptionsConsumed,
    abortConditionalUI,
  } = useContext(WebAuthnContext);
  const { handleVerifyPasskey } = usePasskeySignIn();
  const isLastUsed = useLastUsedSignInMethod()?.type === 'passkey';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isLoadingActive = useDebouncedLoader(isSubmitting, 300);
  const isDisabled = isPreparing || !authenticationOptions || isPasskeyFlowProcessing;

  const preSignInErrorHandler = useSubmitInteractionErrorHandler(InteractionEvent.SignIn, {
    replace: true,
  });

  const onPasskeySignIn = useCallback(async () => {
    if (!authenticationOptions) {
      return;
    }
    // A pending conditional-UI request would make this one fail with
    // `OperationError: A request is already pending`.
    abortConditionalUI();
    setIsSubmitting(true);
    setIsPasskeyFlowProcessing(true);
    try {
      await handleVerifyPasskey(authenticationOptions, preSignInErrorHandler);
    } finally {
      markAuthenticationOptionsConsumed();
      setIsPasskeyFlowProcessing(false);
      setIsSubmitting(false);
    }
  }, [
    abortConditionalUI,
    authenticationOptions,
    handleVerifyPasskey,
    markAuthenticationOptionsConsumed,
    preSignInErrorHandler,
    setIsPasskeyFlowProcessing,
  ]);

  if (!browserSupportsWebAuthn()) {
    return null;
  }

  return (
    <LastUsedFrame isLastUsed={isLastUsed}>
      <Button
        {...optionButtonProps}
        // Disabled only while the passkey options load; fading it would read as unavailable.
        className={classNames(optionButtonClass, 'data-[disabled=true]:opacity-100')}
        isDisabled={isDisabled && !isPreview}
        isPending={isLoadingActive}
        onPress={onPasskeySignIn}
      >
        {isLoadingActive ? (
          // The fingerprint's 20px slot, so the label does not shift when it swaps in.
          <MatrixLoader size="sm" className="size-5" />
        ) : (
          <Fingerprint className="size-5 shrink-0" />
        )}
        <span className={optionLabelClass}>
          {t('action.sign_in_with', { name: t('mfa.webauthn').toLowerCase() })}
        </span>
      </Button>
    </LastUsedFrame>
  );
};

export default PasskeySignInButton;
